import React, { useEffect } from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import IndicatorReportConfigModal from "~/pages/IndicatorReports/modals/IndicatorReportConfigModal";
import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { tableHeader } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/options";
import { tableDataFormatter } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/tableDataFormatter";
import {
  IndicatorGroupList,
  useGetIndicatorGroupsLazyQuery,
  useGetIndicatorReportsLazyQuery,
} from "~/schemas/generated/graphql";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";
import { IndicatorGroupStatus } from "~/types/IndicatorGroupStatus";
import { IndicatorReportStatus } from "~/types/IndicatorReportStatus";

import {
  getIndicatorReportsColumns,
  INDICATOR_REPORTS_SORTABLE_COLUMNS,
} from "./model/tableAdapter";

import s from "./s.less";

const IndicatorReports = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const {
    page,
    count,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();
  // Ключ group приходит из URL и не описан в типе useGetLocationData
  const { group } = useGetLocationData() as Record<string, unknown>;

  // getIndicatorGroups/getIndicatorReports принимают sortField/sortType,
  // поэтому сортировка серверная, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: INDICATOR_REPORTS_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && INDICATOR_REPORTS_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;
  const tableSortType = tableSortField ? sortType : undefined;

  const { handleCount, handlePage } = usePaginationParams();

  const targetView = id ? "reports" : "reportsGroups";

  const [getReportsGroups, reportsGroupsState] = useGetIndicatorGroupsLazyQuery(
    {
      fetchPolicy: "cache-and-network",
    },
  );
  const [getReports, reportsState] = useGetIndicatorReportsLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  const reportsGroups: IndicatorGroupList = get(
    reportsGroupsState,
    "data.IndicatorGroupReportGetter.getIndicatorGroups",
    {},
  );
  const reports: IndicatorGroupList = get(
    reportsState,
    "data.IndicatorGroupReportGetter.getIndicatorReports",
    {},
  );

  const reportsListInstancesResolver = {
    reportsGroups: {
      ...reportsGroups,
      items: tableDataFormatter(reportsGroups.items, t),
    },
    reports: {
      ...reports,
      items: tableDataFormatter(reports.items, t),
    },
  };

  const goToReports = ({ id, nameRu }) =>
    history.push(`/reports/${id}?group=${nameRu}`);

  const openConfigModal = (record: { id: string }) =>
    setDataReplace("openModal", "ReportConfigModal", "currentId", record.id);

  const goBack = () => history.push("/reports/");

  const columns = getIndicatorReportsColumns(
    t,
    tableHeader,
    tableSortField,
    tableSortType,
  );

  useEffect(() => {
    const sortVariables = {
      sortField: tableSortField,
      sortType: tableSortType,
      page: Number(page),
      count: Number(count),
    };
    if (targetView === "reportsGroups")
      getReportsGroups({
        variables: {
          ...sortVariables,
          status: IndicatorGroupStatus.Active,
        },
      });
    else
      getReports({
        variables: {
          ...sortVariables,
          GroupId: id,
          status: IndicatorReportStatus.Active,
        },
      });
  }, [page, count, tableSortField, tableSortType, id]);

  return (
    <Wrapper.Content
      title={
        group
          ? `${t("reports.mainTitle")}: ${t("reports.catalog")} "${group}"`
          : t("reports.mainTitle")
      }
      txtBtn={id ? t("ui.back") : undefined}
      goBackHandler={id ? goBack : undefined}
    >
      <TableV2
        wrapperClassName={s.tableWrapper}
        columns={columns}
        dataSource={reportsListInstancesResolver[targetView].items}
        rowKey="id"
        onRowSettings={id ? openConfigModal : undefined}
        actionsFixed={id ? true : undefined}
        loading={reportsGroupsState.loading || reportsState.loading}
        sortableColumns={INDICATOR_REPORTS_SORTABLE_COLUMNS}
        sortField={tableSortField}
        sortType={tableSortType}
        onSortChange={onSortChange}
        onRow={
          !id
            ? (record) => ({
                onClick: () => goToReports(record),
                style: { cursor: "pointer" },
              })
            : undefined
        }
      />
      <IndicatorReportConfigModal />
      <Pagination
        count={Number(count) || 25}
        page={(Number(page) || 0) + 1}
        totalCount={reportsListInstancesResolver[targetView].total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />
    </Wrapper.Content>
  );
};

export default IndicatorReports;
